import React, { DetailedHTMLProps, HTMLAttributes, useMemo } from 'react'
import { Layout } from '@components/common'
import { getConfig } from '@framework/api'
import getSiteInfo from '@framework/common/get-site-info'
import type { GetStaticPropsContext } from 'next'
import { getPageData } from '@utils/contentful/get-page-data'
import {
  CONTENTFUL_SPACE_IDS,
  loadContentFromAllSpaces,
} from '@utils/contentful'
import { revalidatePeriod } from '@utils/server-side-config'
import { mapContentfulImageAsset } from '@utils/contentful/contentfulImage'
import { GenericContentBlock } from 'types/contentful/genericContentBlock'
import {
  INoissuePlusSectionData,
  NoissuePlusSection,
} from '@components/category/NoissuePlus/NoissuePlusSection'
import { CategoryPage } from 'types/contentful/categoryPage'
import { Entry } from 'contentful'
import { ISeoSectionData } from '@utils/contentful/getSharedPageData/getSharedPageData'
import { trackProductClick, trackProductImpression } from '@utils/gtm'
import { SeoSection } from '@components/category/SeoSection/SeoSection'
import EverestVisibilityDetector from '@components/common/EverestVisibilityDetector'
import { MarketingTile } from '@pageFeatures/category/MarketingTile'
import {
  getProductListFromCategory,
  parseCategoryMarketingCards,
  productDataToProductTile,
} from '@pageFeatures/category/category.utils'
import isEmpty from 'lodash/isEmpty'
import { startRenderTransaction, finishRenderTransaction } from '@utils/tracing'
import { IGenericSEOModel } from 'types/contentful/genericSEOModel'
import { SeoMetaData } from '@components/SeoMetaData/SeoMetaData'
import { ProductTile } from '@pageFeatures/category/ProductTile'
import { useRouter } from 'next/router'
import { TProductTileData } from '@pageFeatures/category/ProductTile/ProductTile.types'
import { IMarketingCategoryCard } from '@pageFeatures/category/category.types'
import { BreadcrumbJsonLd } from 'next-seo'

import {
  Breadcrumbs,
  BreadcrumbsItem,
} from '@noissue-ui-kit/oldBranding/breadcrumbs'
import {
  breadcrumbsLinksToStructuredData,
  getPageBreadcrumbs,
} from '@utils/breadcrumbs'
import { IBreadcrumbLink } from 'types/breadcrumbs'

export async function getStaticProps({
  params,
  preview,
  locale,
}: GetStaticPropsContext<{ slug: string }>) {
  const tx = startRenderTransaction('/category/[slug]', params.slug)

  const config = getConfig({ locale })
  const { categories } = await getSiteInfo({ config, preview })
  const pageData = await getPageData({ categories, preview })

  const categoryPage = (await loadContentFromAllSpaces({
    slug: `/category/${params?.slug}`,
    contentType: 'categoryPage',
    spaceId: CONTENTFUL_SPACE_IDS.ECOMMERCE_1,
    useLocale: true,
    preview,
  })) as Entry<CategoryPage>

  if (isEmpty(categoryPage)) {
    return {
      notFound: true,
    }
  }

  const categoryProductsWithVariants = await getProductListFromCategory({
    categoryPageEntries: categoryPage,
    categories,
  })

  // Filter out marketing cards (Styled Content Block Entries) and parse them separately.
  const categoryMarketingCards = parseCategoryMarketingCards(
    categoryPage?.fields?.productCards
  )

  const noissuePlusSectionData: GenericContentBlock =
    categoryPage?.fields?.noissuePlusSection?.fields

  finishRenderTransaction(tx)

  const categoryProductsWithRelevantData = categoryProductsWithVariants.map(
    (product) => productDataToProductTile(product)
  )

  // Combining products and marketing cards
  const combinedCategoryCards: (
    | TProductTileData
    | IMarketingCategoryCard
    | any
  )[] = categoryProductsWithRelevantData

  for (let card of categoryMarketingCards) {
    combinedCategoryCards.splice(card.index, 0, {
      ...card,
      componentType: 'marketingCard',
    })
  }

  return {
    props: {
      ...pageData,
      combinedCategoryCards: combinedCategoryCards || null,
      categoryPageData: {
        title: categoryPage?.fields?.pageTitleCardTitle || null,
        description: categoryPage?.fields?.pageTitleCardDescription || null,
        backgroundColour:
          categoryPage?.fields?.pageTitleCardBackgroundColour || null,
        imageTitle:
          categoryPage?.fields?.pageTitleCardImage?.fields?.title || null,
        imageUrl:
          categoryPage?.fields?.pageTitleCardImage?.fields?.file?.url || null,
        titleCardLayout: categoryPage?.fields?.pageTitleCardLayout || null,
      },
      noissuePlusSectionData: {
        title: noissuePlusSectionData?.h1 || null,
        description: noissuePlusSectionData?.h2 || null,
        ctaLabel: noissuePlusSectionData?.ctaLabel || null,
        ctaLink: noissuePlusSectionData?.ctaLink || null,
        image:
          mapContentfulImageAsset(noissuePlusSectionData?.primaryImages[0]) ||
          null,
      } as INoissuePlusSectionData,
      seoSectionData: (categoryPage?.fields?.seoSections?.map((seoSection) => ({
        title: seoSection?.fields?.h1,
        description: seoSection?.fields?.h2,
      })) || []) as ISeoSectionData[],
      seoMetaTags: categoryPage?.fields?.seoMetaTags?.fields || null,
    },
    revalidate: revalidatePeriod(), // In seconds
  }
}

export async function getStaticPaths() {
  const paths = [{ params: { slug: 'tissue-and-wrapping' } }]
  return {
    paths,
    fallback: 'blocking',
  }
}

export default function Slug({
  categoryPageData,
  combinedCategoryCards,
  noissuePlusSectionData,
  seoSectionData,
  seoMetaTags,
}: {
  categoryPageData: any
  combinedCategoryCards: (TProductTileData | IMarketingCategoryCard | any)[]
  noissuePlusSectionData: INoissuePlusSectionData
  seoSectionData: ISeoSectionData[]
  seoMetaTags: IGenericSEOModel
}) {
  const router = useRouter()

  const breadcrumbsItems: IBreadcrumbLink[] = useMemo(
    () =>
      getPageBreadcrumbs([
        {
          name: categoryPageData?.title,
          path: router?.asPath,
        },
      ]),
    [categoryPageData]
  )

  return (
    <>
      <SeoMetaData
        seoMetaTags={seoMetaTags}
        fallbackTitle={categoryPageData?.title}
        fallbackDescription={categoryPageData?.description}
      />

      <BreadcrumbJsonLd
        itemListElements={breadcrumbsLinksToStructuredData(breadcrumbsItems)}
      />

      <div className="mx-auto max-w-[1360px] sm:pt-4 sm:px-4 md:px-8">
        {breadcrumbsItems?.length > 0 && (
          <Breadcrumbs className="w-full mx-auto py-6 h-20 max-w-screen-xl pl-8 xl:pl-0">
            {breadcrumbsItems.map((breadcrumb, index) => (
              <BreadcrumbsItem
                key={`breadcrumbsItem-${index}`}
                href={breadcrumb.url}
                text={breadcrumb.name}
              />
            ))}
          </Breadcrumbs>
        )}

        <div className="text-acai flex flex-col pt-10 pb-16 lg:py-10 px-8">
          <h1 className="font-mori font-semibold text-5xl lg:text-6xl mb-2 leading-snug">
            {categoryPageData?.title}
          </h1>

          <p className="text-2xl text-boulder max-w-full md:max-w-[75%] font-mori">
            {categoryPageData?.description}
          </p>
        </div>

        <ul
          className="px-2 lg:px-0 gap-[14px 0] sm:gap-[14px 14px] min-h-[440px]"
          style={
            {
              display: 'grid',
              'grid-template-columns': 'repeat(auto-fill,minmax(312px,1fr))',
              'grid-template-rows': `repeat(auto-fill,1fr)`,
              'grid-auto-flow': 'dense',
              'min-height': `${
                440 * Math.ceil(combinedCategoryCards?.length / 4)
              }px`,
            } as DetailedHTMLProps<
              HTMLAttributes<HTMLUListElement>,
              HTMLUListElement
            >
          }
        >
          {combinedCategoryCards?.map((card, index) => {
            if (card.componentType === 'marketingCard') {
              return (
                <EverestVisibilityDetector
                  key={card.title}
                  once
                  onVisible={() => {
                    trackProductImpression({
                      list: 'Category page',
                      item: {
                        name: card.title as string,
                        slug: card.link as string,
                        componentType: card.componentType as string,
                      },
                      position: index,
                    })
                  }}
                >
                  <li
                    className={`w-full list-none px-4 sm:p-4 mb-6 sm:mb-0 ${
                      card.desktopLayout === 'feature'
                        ? 'col-span-1 sm:col-span-2'
                        : 'col-span-1'
                    }`}
                  >
                    <MarketingTile
                      backgroundColor={card?.backgroundColor}
                      title={card?.title}
                      titleColor={card.titleColor}
                      titleSize={card.titleSize}
                      subtitle={card?.subtitle}
                      subtitleColor={card.subtitleColor}
                      desktopImage={card.desktopImage}
                      mobileImage={card.mobileImage}
                      link={card.link}
                      desktopLayout={card.desktopLayout}
                      mobileLayout={card.mobileLayout}
                      ctaLabel={card.ctaLabel}
                    />
                  </li>
                </EverestVisibilityDetector>
              )
            } else {
              return (
                <EverestVisibilityDetector
                  key={card.productName}
                  once
                  onVisible={() => {
                    trackProductImpression({
                      list: 'Category page',
                      item: {
                        name: card.productName as string,
                        productSlug: card.productSlug as string,
                        price: card.price?.toString() || '',
                      },
                      position: index,
                    })
                  }}
                >
                  <li className={`z-2 hover:z-5 w-full`}>
                    <ProductTile
                      linkUrl={card.linkUrl}
                      productName={card.productName}
                      pricePerUnit={card.pricePerUnit}
                      salePricePerUnit={card.salePricePerUnit}
                      primaryImage={card.primaryImage}
                      imageBackgroundColor={card.imageBackgroundColor}
                      imageTags={card.imageTags}
                      productTags={card.productTags}
                      productDetails={card.productDetails}
                      productType={card.productType}
                      priorityLoad={index < 4}
                      onClick={(event) => {
                        event.preventDefault()
                        trackProductClick({
                          list: 'Category Page',
                          buttonText: '',
                          item: {
                            name: card.productName as string,
                            productSlug: card.productSlug as string,
                            pricePerUnit: card.pricePerUnit?.toString() || '',
                          },
                          position: index,
                        })
                        router.push(card.linkUrl).catch(() => {})
                      }}
                    />
                  </li>
                </EverestVisibilityDetector>
              )
            }
          })}
        </ul>

        {(noissuePlusSectionData?.title ||
          noissuePlusSectionData?.description) && (
          <div className="mx-8 py-12 sm:mx-4 md:mx-0">
            <NoissuePlusSection {...noissuePlusSectionData} />
          </div>
        )}

        {seoSectionData?.length > 0 && (
          <div className="mx-8 py-12 sm:mx-4 md:mx-0">
            <SeoSection seoSectionData={seoSectionData} />
          </div>
        )}
      </div>
    </>
  )
}

Slug.Layout = Layout
